import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { App, Button, Card, Col, InputNumber, Row, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import {
  sellerWalletAPI,
  type SellerPayoutRequestItem,
  type SellerWalletSummaryResponse,
  type SellerWalletTransactionItem,
} from '../utils/sellerApi'
import { useSellerAuth } from '../contexts/SellerAuthContext'

const toAmount = (value: string | number | undefined | null): number => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

const formatMoney = (value: string | number | undefined | null, currency: string) => {
  const amount = toAmount(value)

  return `${currency} ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const WalletPage = () => {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState<SellerWalletSummaryResponse | null>(null)
  const [payoutAmount, setPayoutAmount] = useState<number | null>(null)
  const [requestingPayout, setRequestingPayout] = useState(false)
  const { hasPermission } = useSellerAuth()
  const { message } = App.useApp()

  const canRequestPayout = hasPermission('wallet.request')

  const wallet = summary?.wallet
  const currency = wallet?.currency || 'INR'

  const fetchWallet = useCallback(async () => {
    setLoading(true)

    try {
      const response = await sellerWalletAPI.summary({ limit: 20, payout_limit: 20 })
      setSummary(response.data.data)
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load wallet data')
    } finally {
      setLoading(false)
    }
  }, [message])

  useEffect(() => {
    void fetchWallet()
  }, [fetchWallet])

  const requestPayout = async () => {
    if (!canRequestPayout) {
      message.warning('You are not permitted to request payouts.')
      return
    }

    const amount = Number(payoutAmount)

    if (!Number.isFinite(amount) || amount <= 0) {
      message.warning('Enter a valid payout amount')
      return
    }

    setRequestingPayout(true)

    try {
      await sellerWalletAPI.requestPayout(amount)
      message.success('Payout request submitted')
      setPayoutAmount(null)
      await fetchWallet()
    } catch (error: any) {
      message.error(error.response?.data?.error || error.response?.data?.message || 'Failed to submit payout request')
    } finally {
      setRequestingPayout(false)
    }
  }

  const downloadTransactionsCsv = async () => {
    try {
      const response = await sellerWalletAPI.exportTransactionsCsv()
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `seller-wallet-transactions-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      message.success('Wallet transactions CSV downloaded.')
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to download wallet transactions CSV')
    }
  }

  const transactionColumns: ColumnsType<SellerWalletTransactionItem> = useMemo(
    () => [
      {
        title: 'Date',
        key: 'created_at',
        render: (_, row) => (row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A'),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        render: (value: string) => <Tag color="blue">{value}</Tag>,
      },
      {
        title: 'Direction',
        dataIndex: 'direction',
        key: 'direction',
        render: (value: string) => (
          <Tag color={value === 'credit' ? 'green' : 'volcano'}>{value.toUpperCase()}</Tag>
        ),
      },
      {
        title: 'Gross',
        key: 'gross_amount',
        render: (_, row) => formatMoney(row.gross_amount, row.currency || currency),
      },
      {
        title: 'Platform Fee',
        key: 'platform_fee_amount',
        render: (_, row) => formatMoney(row.platform_fee_amount, row.currency || currency),
      },
      {
        title: 'Net',
        key: 'net_amount',
        render: (_, row) => formatMoney(row.net_amount, row.currency || currency),
      },
      {
        title: 'Balance After',
        key: 'balance_after',
        render: (_, row) => formatMoney(row.balance_after, row.currency || currency),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
      },
    ],
    [currency],
  )

  const payoutColumns: ColumnsType<SellerPayoutRequestItem> = useMemo(
    () => [
      {
        title: 'Payout Date',
        key: 'payout_date',
        render: (_, row) => (row.payout_date ? new Date(row.payout_date).toLocaleDateString() : '-'),
      },
      {
        title: 'Requested At',
        key: 'requested_at',
        render: (_, row) => (row.requested_at ? new Date(row.requested_at).toLocaleString() : 'N/A'),
      },
      {
        title: 'Amount',
        key: 'amount',
        render: (_, row) => formatMoney(row.amount, row.currency || currency),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (value: string) => {
          const color = value === 'paid' ? 'green' : value === 'rejected' ? 'red' : 'gold'
          return <Tag color={color}>{value.toUpperCase()}</Tag>
        },
      },
      {
        title: 'Reviewed At',
        key: 'reviewed_at',
        render: (_, row) => (row.reviewed_at ? new Date(row.reviewed_at).toLocaleString() : 'Pending'),
      },
      {
        title: 'Rejection Reason',
        dataIndex: 'rejection_reason',
        key: 'rejection_reason',
        render: (value?: string | null) => value || '-',
      },
    ],
    [currency],
  )

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Wallet
          </Typography.Title>
          <Typography.Text type="secondary">
            Track seller earnings, platform fee split, payout requests, and transaction history.
          </Typography.Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => void fetchWallet()} loading={loading}>
          Refresh
        </Button>
        <Button icon={<DownloadOutlined />} onClick={() => void downloadTransactionsCsv()} disabled={loading}>
          Transactions CSV
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Typography.Text type="secondary">Available Balance</Typography.Text>
            <Typography.Title level={4} style={{ margin: '8px 0 0 0' }}>
              {formatMoney(wallet?.available_balance, currency)}
            </Typography.Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Typography.Text type="secondary">Pending Payout Balance</Typography.Text>
            <Typography.Title level={4} style={{ margin: '8px 0 0 0' }}>
              {formatMoney(wallet?.pending_payout_balance, currency)}
            </Typography.Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Typography.Text type="secondary">Total Credited</Typography.Text>
            <Typography.Title level={4} style={{ margin: '8px 0 0 0' }}>
              {formatMoney(wallet?.total_credited, currency)}
            </Typography.Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Typography.Text type="secondary">Platform Fee %</Typography.Text>
            <Typography.Title level={4} style={{ margin: '8px 0 0 0' }}>
              {(summary?.platform_fee_percentage ?? 0).toFixed(2)}%
            </Typography.Title>
          </Card>
        </Col>
      </Row>

      <Card loading={loading} title="Request Payout">
        <Space wrap>
          <InputNumber
            min={0.01}
            step={1}
            precision={2}
            value={payoutAmount ?? undefined}
            onChange={(value) => setPayoutAmount(typeof value === 'number' ? value : null)}
            placeholder="Enter payout amount"
            style={{ width: 240 }}
            disabled={!canRequestPayout}
          />
          <Button type="primary" onClick={() => void requestPayout()} loading={requestingPayout} disabled={!canRequestPayout}>
            Submit Payout Request
          </Button>
          <Typography.Text type="secondary">
            Available: {formatMoney(wallet?.available_balance, currency)}
          </Typography.Text>
          {!canRequestPayout ? (
            <Typography.Text type="secondary" style={{ display: 'block' }}>
              You have read-only wallet access.
            </Typography.Text>
          ) : null}
        </Space>
      </Card>

      <Card title="Recent Wallet Transactions" loading={loading}>
        <Table<SellerWalletTransactionItem>
          rowKey="id"
          columns={transactionColumns}
          dataSource={summary?.transactions ?? []}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 980 }}
        />
      </Card>

      <Card title="Payout Requests" loading={loading}>
        <Table<SellerPayoutRequestItem>
          rowKey="id"
          columns={payoutColumns}
          dataSource={summary?.payout_requests ?? []}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 900 }}
        />
      </Card>
    </Space>
  )
}

export default WalletPage
